import {Component, inject, Input, OnDestroy, OnInit, computed, input, signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {CoreService} from 'src/app/core/services/core.service';
import {MedicationItemAddCardComponent} from '@xmedical/clinical-shared';
import {MatDialog} from '@angular/material/dialog';
import {libraryProductDescription, MasterDataRepositoryService, Medication, MedicineLibraryProductMatch, Patient, ScriptType, UserSettings} from '@xmedical/core';
import {MedicationAddedEvent, MedicationDisplayItem, MedicationRemovedEvent, MedicationSearchEntry} from '@xmedical/shared';
import {PatientMedicationStore, PatientScriptsStore, PatientStore} from '../../../core/state';

@Component({
    selector: 'app-patient-medication-widget',
    templateUrl: './medication-widget.component.html',
    styleUrls: ['./medication-widget.component.scss'],
    standalone: false
})
export class MedicationWidgetComponent implements OnInit, OnDestroy {

  @Input() title: string;
  @Input() scriptType: ScriptType;
  /**
   * Whether this widget offers the "Save Script" action. Configured per widget in the practice's
   * clinical layout; defaults to true so a widget with no configured value keeps the pre-toggle
   * behaviour.
   */
  allowScriptPrinting = input(true);
  error = '';
  loading = false;
  settings: UserSettings;
  private subscriptions: Subscription[] = [];

  private masterDataRepository = inject(MasterDataRepositoryService);
  private matDialog = inject(MatDialog);
  private coreService = inject(CoreService);
  private patientStore = inject(PatientStore);
  protected medicationStore = inject(PatientMedicationStore);
  private scriptsStore = inject(PatientScriptsStore);

  isProcessing = signal(false);

  canAdd = computed(() => this.coreService.hasPermission('Clinical.Medication.Write'));
  canDelete = computed(() => this.coreService.hasPermission('Clinical.Medication.Write'));
  canSaveScript = computed(() => this.coreService.hasPermission('Clinical.Scripts.Write'));

  /** Items filtered to this widget's scriptType. */
  readonly medication = computed<Medication[]>(() =>
    this.medicationStore.items().filter(item => item.scriptType === this.scriptType)
  );

  medicationItems = computed<MedicationDisplayItem[]>(() =>
    this.medication().map(item => ({
      id: item.id,
      description: item.description,
      dosage: item.dosage,
      diagnosisCode: item.diagnosisCode,
      comment: item.comment,
      lastModifiedDate: item.lastModifiedDate,
      modifiedDateUtc: item.modifiedDateUtc,
      lastModifiedUser: item.lastModifiedUser,
      typeId: item.typeId,
      typeUnassigned: item.type?.unassigned,
      textColour: item.textColour,
      // Worked out on the server from the whole list (v2, #333); the list view shows it as red or yellow.
      overlap: item.overlap,
      overlapReason: item.overlapReason

    }))
  );

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasMedication(): boolean {
    return this.medication().length > 0;
  }

  get patientId(): number {
    return this.patientStore.current()?.id ?? 0;
  }

  get patient(): Patient | null {
    return this.patientStore.current();
  }

  get confirmationMessage(): string {
    return 'Are you sure you want to save a chronic script with this medication for ' + this.patient?.firstName + ' ' + this.patient?.surname + '?';
  }

  // v2 search: the practice list, plus the medicine library when the practice has it on (#333).
  searchMedication$ = (query: string): Observable<MedicationSearchEntry[]> => {
    // The whole search already needs Clinical.Medication.Write (canAdd), so the library is always included.
    return this.medicationStore.search(query, true);
  }

  ngOnInit(): void {
    this.settings = this.coreService.settings();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onAddNewItem(searchTerm: string) {
    this.openAddCard(searchTerm, null);
  }

  // A library product: the same add card as "add new", with the product's name and strength filled in. The
  // practice-list item it creates is linked to the product, and the patient's medication copies that link.
  onLibraryProductSelected(product: MedicineLibraryProductMatch) {
    this.openAddCard(libraryProductDescription(product), product.productId);
  }

  private openAddCard(searchTerm: string, productId: number | null) {
    const dialogRef = this.matDialog.open(MedicationItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
      width: '700px',
      data: {
        searchTerm,
        diagnosisCodeSearch: (q: string) => this.masterDataRepository.searchDiagnosisCodes(q),
      },
    });

    // Card now returns the form value via dialogRef.close (no id). Persist
    // it to the master medications list to obtain an id, then run the
    // existing patient-attach flow via the medication store.
    this.subscriptions.push(dialogRef.afterClosed().pipe(
      filter((v): v is Medication => !!v),
      // Only a library pick carries a link; free text from "add new" is saved exactly as entered.
      map(medication => productId === null ? medication : ({...medication, productId} as Medication)),
      switchMap(medication => this.medicationStore.saveToPracticeList(medication)),
    ).subscribe(medication => this.onItemSelected(medication)));
  }

  onItemAdded(event: MedicationAddedEvent) {
    const medication = event.item as Medication;
    medication.comment = event.comment;
    this.onItemSelected(medication);
  }

  onItemSelected(medication: Medication) {
    medication.createdUser = this.coreService.currentUser;
    medication.lastModifiedUser = this.coreService.currentUser;
    medication.createdDate = new Date();
    medication.createdDateUtc = new Date();
    medication.lastModifiedDate = new Date();
    medication.modifiedDateUtc = new Date();
    medication.scriptType = this.scriptType;
    this.error = '';
    this.subscriptions.push(this.medicationStore.add(medication).subscribe({
      error: () => {
        this.error = 'Could not add new medication item to this patient';
      }
    }));
  }

  saveScript() {
    this.subscriptions.push(
      this.scriptsStore.createFromMedication(this.medication(), this.scriptType).subscribe({
        error: (error) => {
          this.coreService.showErrorToast('Could not save script');
          console.error(error);
        }
      })
    );
  }

  onItemRemoved(event: MedicationRemovedEvent) {
    const id = event.item.id;
    if (!id) return;
    this.isProcessing.set(true);
    this.subscriptions.push(this.medicationStore.remove(id).subscribe({
      next: () => this.isProcessing.set(false),
      error: () => {
        this.coreService.showErrorToast('Could not remove medication item');
        this.isProcessing.set(false);
      }
    }));
  }
}
